import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  SESSION_IDLE_WARN_MS,
  bindSessionActivityListeners,
  bindSharedSessionActivitySync,
  isAssistantSessionPath,
  readSharedSessionActivity,
  resolveEffectiveLastActivity,
  resolveSessionIdleMs,
  touchSharedSessionActivity,
} from "@/lib/sessionIdle";

export default function SessionIdleGuard({
  user,
  sessionRemember,
  logout,
  pathname = "",
  children,
}) {
  const assistantMode = isAssistantSessionPath(pathname);
  const [warnOpen, setWarnOpen] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const warnShownRef = useRef(false);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const applyActivity = useCallback((at) => {
    const ts = typeof at === "number" && at > 0 ? at : touchSharedSessionActivity();
    lastActivityRef.current = ts;
    warnShownRef.current = false;
    setWarnOpen(false);
  }, []);

  const resetActivity = useCallback(() => {
    applyActivity(touchSharedSessionActivity());
  }, [applyActivity]);

  useEffect(() => {
    if (!user) {
      setWarnOpen(false);
      return undefined;
    }
    applyActivity(touchSharedSessionActivity());
    const unbindDom = bindSessionActivityListeners(resetActivity);
    const unbindShared = bindSharedSessionActivitySync((ts) => {
      if (ts >= lastActivityRef.current) applyActivity(ts);
    });
    return () => {
      unbindDom();
      unbindShared();
    };
  }, [user, resetActivity, applyActivity]);

  useEffect(() => {
    if (!user || assistantMode) return undefined;

    const idleMs = resolveSessionIdleMs(sessionRemember);
    const tick = window.setInterval(() => {
      const last = resolveEffectiveLastActivity(lastActivityRef.current, readSharedSessionActivity());
      lastActivityRef.current = last;
      const elapsed = Date.now() - last;
      const remaining = idleMs - elapsed;
      if (remaining <= 0) {
        setWarnOpen(false);
        logoutRef.current?.();
        return;
      }
      if (remaining <= SESSION_IDLE_WARN_MS && !warnShownRef.current) {
        warnShownRef.current = true;
        setWarnOpen(true);
      }
    }, 15000);

    return () => window.clearInterval(tick);
  }, [user, sessionRemember, assistantMode]);

  return (
    <>
      {children}
      {!assistantMode ? (
        <AlertDialog open={warnOpen} onOpenChange={setWarnOpen}>
          <AlertDialogContent data-testid="session-idle-warning">
            <AlertDialogHeader>
              <AlertDialogTitle>Sesión por inactividad</AlertDialogTitle>
              <AlertDialogDescription>
                No hubo actividad en los últimos minutos. Por seguridad, la sesión se cerrará en unos
                minutos si no continúa trabajando.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={resetActivity} data-testid="session-idle-stay-btn">
                Seguir conectado
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </>
  );
}
